/**
 * Google Identity Services のスクリプトを読み込んで、ボタンを描画する。
 * - clientId が未設定なら何も出さない
 * - credential を取得したら onCredential を呼ぶ
 */

import { useEffect, useRef, useState } from "react";
import { fetchGoogleConfig } from "../api";

const GIS_SRC = "https://accounts.google.com/gsi/client";

interface Props {
  onCredential: (credential: string) => void;
  onError?: (msg: string) => void;
  text?: "signin_with" | "signup_with" | "continue_with" | "signin";
}

interface GsiId {
  initialize: (opts: {
    client_id: string;
    callback: (resp: { credential: string }) => void;
    auto_select?: boolean;
    ux_mode?: "popup" | "redirect";
  }) => void;
  renderButton: (
    el: HTMLElement,
    opts: {
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "small" | "medium" | "large";
      shape?: "rectangular" | "pill" | "circle" | "square";
      width?: number;
      text?: Props["text"];
    },
  ) => void;
}

interface GlobalGoogle {
  accounts: { id: GsiId };
}

export function GoogleSignInButton({ onCredential, onError, text = "continue_with" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);

  // 1) /api/auth/google/config から clientId を取得
  useEffect(() => {
    let canceled = false;
    fetchGoogleConfig()
      .then((c) => {
        if (canceled) return;
        setAvailable(c.available);
        setClientId(c.clientId);
      })
      .catch(() => {
        if (canceled) return;
        setAvailable(false);
      });
    return () => {
      canceled = true;
    };
  }, []);

  // 2) GIS スクリプト挿入
  useEffect(() => {
    if (!clientId) return;
    if (document.getElementById("google-gsi-script")) return;
    const s = document.createElement("script");
    s.id = "google-gsi-script";
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    document.body.appendChild(s);
  }, [clientId]);

  // 3) スクリプトが読めたら initialize + renderButton
  useEffect(() => {
    if (!clientId || !containerRef.current) return;
    let stopped = false;
    let timer: number | null = null;

    const tryInit = () => {
      const g = (window as unknown as { google?: GlobalGoogle }).google;
      if (!g?.accounts?.id) return false;
      try {
        g.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => {
            if (resp.credential) onCredential(resp.credential);
            else onError?.("認証応答に credential が含まれていません");
          },
          ux_mode: "popup",
        });
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          g.accounts.id.renderButton(containerRef.current, {
            theme: "filled_black",
            size: "large",
            shape: "rectangular",
            width: 320,
            text,
          });
        }
      } catch (e) {
        onError?.(`Google ログイン初期化失敗: ${(e as Error).message}`);
      }
      return true;
    };

    if (tryInit()) return;
    timer = window.setInterval(() => {
      if (stopped) return;
      if (tryInit()) {
        if (timer !== null) window.clearInterval(timer);
      }
    }, 100);
    return () => {
      stopped = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [clientId, onCredential, onError, text]);

  if (available === false) {
    return null; // 設定が無ければ何も出さない
  }

  return (
    <div
      ref={containerRef}
      className="gsi-button"
      style={{ minHeight: 44, display: "flex", justifyContent: "center" }}
    />
  );
}
